import { create } from 'apisauce';

import { formatFormData } from '../lib/data';
import { TExportFormat } from '../types/ListDisplay';

// Настройки заголовков и базового url
export const apiWrapper = create({
  baseURL: `/api/v1/`,
  headers: {
    'Content-Type': 'application/json'
  },
  transformRequest: [
    (data, headers) => {
      // Если заголовок для форм даты есть, то применяем форматирование
      if (headers['Content-Type'] === 'multipart/form-data') {
        return formatFormData(data);
      }
      return JSON.stringify(data);
    }
  ]
});

/** Query-параметры списков галереи */
export interface IGalleryListQuery {
  /** Режим предпросмотра: владелец видит галерею как кинокомпания (без отклонённых модерацией) */
  preview?: boolean;
  /** Токен доступа по ссылке (режим link_only) */
  profileToken?: string | null;
}

/**
 * Собирает query-строку списков галереи; пустые значения опускаются
 * @param query - параметры
 * @param query.preview - режим предпросмотра владельцем
 * @param query.profileToken - токен доступа по ссылке
 */
const galleryListQuery = ({
  preview,
  profileToken
}: IGalleryListQuery = {}) => {
  const params = new URLSearchParams();
  if (preview) params.set('preview', 'true');
  if (profileToken) params.set('profile_token', profileToken);
  const search = params.toString();

  return search ? `?${search}` : '';
};

export const API_URLS = {
  auth: {
    sign_in: 'auth/sign_in',
    reset_password: 'auth/reset_password'
  },
  actorCards: {
    list: 'actor_cards',
    create: 'actor_cards',
    /**
     * Данные визитки актёра
     * @param id - id визитки
     */
    show: (id: number) => `actor_cards/${id}`,
    /**
     * Редактирование визитки актёра
     * @param id - id визитки
     */
    update: (id: number) => `actor_cards/${id}`,
    /**
     * Удаление визитки актёра
     * @param id - id визитки
     */
    destroy: (id: number) => `actor_cards/${id}`,
    /**
     * Выгрузка реестра визиток одним файлом. Формат — сегментом пути
     * (`export.xlsx` / `export.pdf`), как того требует бэкенд
     * @param format - формат файла
     */
    export: (format: string) => `actor_cards/export.${format}`,
    /**
     * Подтверждение визитки актёром
     * @param id - id визитки
     */
    confirm: (id: number) => `actor_cards/${id}/confirm`,
    /**
     * Отклонение визитки актёром
     * @param id - id визитки
     */
    reject: (id: number) => `actor_cards/${id}/reject`
  },
  bookmarks: {
    /**
     * Добавить кастинг в избранное
     * @param id - id кастинга
     */
    addCasting: (id: number) => `bookmarks/add_casting/${id}`,
    /**
     * Добавить юзера в избранное
     * @param id - id юзера
     */
    addUser: (id: number) => `bookmarks/add_user/${id}`,
    /**
     * Удалить кастинг из избранного
     * @param id - id кастинга
     */
    removeCasting: (id: number) => `bookmarks/remove_casting/${id}`,
    /**
     * Удалить юзера из избранного
     * @param id - id юзера
     */
    removeUser: (id: number) => `bookmarks/remove_user/${id}`,
    list: {
      users: 'bookmarks/users',
      castings: 'bookmarks/castings'
    },
    /**
     * Выгрузка избранного (общего или конкретной папки) одним файлом. Формат —
     * сегментом пути (`export.xlsx` / `export.pdf` / `export.docx`), как того
     * требует бэкенд (двойник `actorCards.export`)
     * @param format - формат файла
     */
    exportUsers: (format: string) => `bookmarks/users/export.${format}`
  },
  castings: {
    /**
     * Создание кастинга
     * @param id - id проекта
     */
    create: (id: string) => `projects/${id}/castings`,
    /**
     * Завершить кастинг
     * @param id - id кастинга
     */
    complete: (id: number) => `castings/${id}/complete`,
    /**
     * Опубликовать кастинг
     * @param id - id проекта
     */
    publish: (id: number) => `castings/${id}/publish`,
    /**
     * Актер покидает кастинг
     * @param id - id кастинга
     */
    leave: (id: number) => `castings/${id}/leave`,
    classifiers: 'castings/classifiers',
    list: {
      all: 'castings',
      new: 'castings/recent',
      forInvitation: 'castings/for_invitation',
      for_select: 'castings/list_for_select',
      /**
       * Получение кастингов режиссера, на которые откликнулся пользователь
       * @param id - id Актера
       */
      responded: (id: string) => `castings/responded/${id}`
    },
    news: {
      /**
       * Получение и добавление новостей кастинга
       * @param castingId - ID кастинга
       */
      list: (castingId: number) => `castings/${castingId}/news`,
      item: {
        /**
         * Архивация новости
         * @param castingId - ID кастинга
         * @param newsId - ID новости
         */
        deactivate: (castingId: number, newsId: number) =>
          `castings/${castingId}/news/${newsId}/deactivate`
      }
    },
    responses: {
      /**
       * Откликнувшиеся на кастинг
       * @param id - id кастинга
       */
      list: (id: string): string => `castings/${id}/responses`,
      /**
       * Одобрить отклик
       * @param id - id отклика
       */
      approve: (id: number): string => `responses/${id}/approve`,
      /**
       * Отклонить отклик
       * @param id - id отклика
       */
      reject: (id: number): string => `responses/${id}/reject`,
      /**
       * Получить данные об отклике
       * @param id - id отклика
       */
      show: (id: number): string => `responses/${id}/`
    },
    /**
     * Откликнуться на кастинг
     * @param id - id кастинга
     */
    respond: (id: number) => `castings/${id}/respond`,
    roles: {
      list: 'casting_roles'
    },
    search: 'castings/search',
    /**
     * Данные кастинга
     * @param castingId - id кастинга
     */
    show: (castingId: number) => `castings/${castingId}`,
    /**
     * Редактирование кастинга
     * @param projectId - id проекта
     * @param castingId - id кастинга
     */
    edit: (projectId: number, castingId: number) =>
      `projects/${projectId}/castings/${castingId}`,
    /**
     * Копировать кастинг
     * @param projectId - id проекта
     * @param castingId - id кастинга
     */
    copy: (projectId: number, castingId: number) =>
      `projects/${projectId}/castings/${castingId}/copy`,
    /**
     * Удаление фотографий кастинга
     * @param castingId - id кастинга
     */
    destroyPhotos: (castingId: string) =>
      `castings/${castingId}/destroy_photos`,
    /**
     * Удаление дополнительных материалов кастинга
     * @param castingId - id кастинга
     */
    destroyMaterials: (castingId: string) =>
      `castings/${castingId}/destroy_materials`
  },
  cities: {
    list: 'cities',
    top: 'cities/top'
  },
  countries: {
    list: 'countries'
  },
  educational_institutions: {
    list: 'educational_institutions'
  },
  faculties: {
    list: 'faculties'
  },
  workshops: {
    list: 'workshops'
  },
  companies: {
    list: 'companies',
    /**
     * Данные компании
     * @param id Id компании
     */
    show: (id: string) => `companies/${id}`
  },
  documents: 'documents',
  excerpts: {
    list: 'excerpts'
  },
  filmmakers: {
    list: 'filmmakers',
    directors: 'filmmakers/directors',
    producers: 'filmmakers/producers',
    scriptwriters: 'filmmakers/scriptwriters'
  },
  films: {
    list: 'films'
  },
  invitations: {
    list: 'invitations',
    /**
     * Эндпоинт для действий с конкретным приглашением
     * @param id - id приглашения
     */
    show: (id: number) => `invitations/${id}`,
    /**
     * Принятие приглашения
     * @param id - id приглашения
     */
    accept: (id: number) => `invitations/${id}/accept`,
    /**
     * Отклонение приглашения
     * @param id - id приглашения
     */
    reject: (id: number) => `invitations/${id}/reject`
  },
  projects: {
    list: {
      all: 'projects',
      forInvitation: 'projects/for_invitation',
      for_select: 'projects/list_for_select'
    },
    /**
     * Завершить проект
     * @param id - id проекта
     */
    complete: (id: number) => `projects/${id}/complete`,
    categories: 'project_categories',
    /**
     * Опубликовать проект
     * @param id - id проекта
     */
    publish: (id: number) => `projects/${id}/publish`,
    /**
     * Данные проекта
     * @param id Id проекта
     */
    show: (id: number) => `projects/${id}`,
    /**
     * Файл отчёта по проекту (19293): формат — сегментом пути
     * @param id - id проекта
     * @param format - формат файла (pdf, xlsx либо docx)
     */
    report: (id: number, format: TExportFormat) =>
      `projects/${id}/report.${format}`,
    /**
     * Сводка отчёта по проекту для строки «Войдёт в отчёт»
     * @param id - id проекта
     */
    reportSummary: (id: number) => `projects/${id}/report/summary`,
    /** Подсказки свободных списков проекта: `?field=genres|countries|studios&query=` (19342) */
    fieldValues: 'projects/field_values',
    /**
     * Копирование проекта
     * @param id - id проекта
     */
    copy: (id: number) => `projects/${id}/copy`,
    participants: {
      /**
       * Добавление участника проекта
       * @param projectId - id проекта
       */
      add: (projectId: number) => `projects/${projectId}/participants`,
      /**
       * Удаление участника проекта
       * @param projectId - id проекта
       * @param id - id связи участника
       */
      remove: (projectId: number, id: number) =>
        `projects/${projectId}/participants/${id}`,
      /**
       * Изменение порядка участников проекта
       * @param projectId - id проекта
       */
      reorder: (projectId: number) =>
        `projects/${projectId}/participants/reorder`,
      /**
       * Жалоба на участника проекта (18258)
       * @param projectId - id проекта
       * @param id - id связи участника
       */
      complaint: (projectId: number, id: number) =>
        `projects/${projectId}/participants/${id}/complaint`
    },
    top: 'projects/top'
  },
  registration: {
    classifiers: 'registration/classifiers',
    actor: {
      start: 'registration/actor_start',
      /**
       * Заполнение деталей профиля
       * @param id Id пользователя
       */
      accountDetails: (id: number) =>
        `registration/actor/account_details/${id}`,
      /**
       * Отправка кода подтверждения
       * @param userId id пользователя
       */
      confirmationCode: (userId: number) =>
        `registration/actor/confirmation_code/${userId}`,
      /**
       * Сохранение системного фото
       * @param userId id пользователя
       */
      systemPhoto: (userId: number) =>
        `registration/actor/system_photo/${userId}`
    },
    director: {
      start: 'registration/director_start',
      /**
       * Отправка кода подтверждения
       * @param userId id пользователя
       */
      confirmationCode: (userId: number) =>
        `registration/director/confirmation_code/${userId}`
    }
  },
  users: {
    /**
     * @param id - id пользователя
     */
    confirmationCode: (id: number) => `users/${id}/confirmation_code`,
    findSimilarities: 'users/find_similarities',
    item: {
      /**
       * Данные пользователя
       * @param id Id пользователя
       */
      show: (id: number) => `users/${id}`,
      /**
       * Предпросмотр своего профиля глазами кинокомпании (18558)
       * @param id Id пользователя
       */
      preview: (id: number) => `users/${id}/preview`,
      /**
       * Данные окна быстрого просмотра анкеты (18967): контакты актёра.
       * Остальное окно берёт из ответа списка и из videos_list
       * @param id Id пользователя
       */
      quickView: (id: number) => `users/${id}/quick_view`
    },
    /**
     * Личная заметка режиссёра об актёре (19447): GET и PUT, видна только автору
     * @param id - id актёра
     */
    note: (id: number) => `users/${id}/note`,
    /**
     * Жалоба режиссёра на анкету актёра (19450): только POST, каждая отправка — новая жалоба
     * @param id - id актёра
     * @param profileToken - токен доступа по ссылке (анкета «Доступ по ссылке»)
     */
    complaint: (id: number, profileToken?: string | null) =>
      `users/${id}/complaint${galleryListQuery({ profileToken })}`,
    /**
     * Настройка уведомлений
     * @param id - id пользователя
     */
    setupNotifications: (id: number) => `users/${id}/set_up_notifications`,
    /**
     * Настройки уведомлений
     * @param id - id пользователя
     */
    notificationSettings: (id: number) => `users/${id}/notification_settings`,
    top: 'users/top',
    videos: {
      /**
       * Список видео пользователя
       * @param id Id пользователя
       * @param query - preview / profile_token
       */
      list: (id: number, query?: IGalleryListQuery) =>
        `users/${id}/videos_list${galleryListQuery(query)}`
    },
    photo: {
      /**
       * Список фотографий пользователя
       * @param id Id пользователя
       * @param query - preview / profile_token
       */
      list: (id: number, query?: IGalleryListQuery) =>
        `users/${id}/images_list${galleryListQuery(query)}`
    },
    /**
     * Формирование и скачивание PDF-версии портфолио
     * @param id Id пользователя
     */
    portfolioPdf: (id: number) => `users/${id}/portfolio_pdf`,
    media: {
      /**
       * Удаление картинки из галереи
       * @param userId - Id пользователя
       * @param mediaId - Id медиафайла
       */
      show: (userId: number, mediaId: number) =>
        `users/${userId}/destroy_media/${mediaId}`
    },
    search: 'users/search',
    /**
     * Обновление системного фото
     * @param id - id пользователя
     */
    setSystemPhoto: (id: number) => `users/${id}/set_system_photo`,
    /**
     * Назначение фото профиля (аватара) из фотографии портфолио
     * @param id - id пользователя
     */
    setProfilePhotoFromImage: (id: number) =>
      `users/${id}/set_profile_photo_from_image`,
    /**
     * Пересортировка фото пользователя
     * @param userId - id пользователя
     */
    reorder_photos: (userId: number) => `users/${userId}/reorder_photos`,
    responses: {
      /**
       * Получение информации об отклике в проекте, и также удаление отклика
       * @param id - Id отклика на проект (response_id)
       */
      show: (id: number) => `responses/${id}`
    },
    email_set_start: 'users/email/set/start',
    email_set_confirm: 'users/email/set/confirm',
    email_change_start: 'users/email/change/start',
    email_change_confirm: 'users/email/change/confirm',
    email_change_finish: 'users/email/change/finish',
    email_resend: 'users/email/resend_email_confirmation',
    /**
     * Подтверждение почты
     * @param verify_code - проверочный код
     */
    confirm_email: 'users/verify_email',
    phone_change_start: 'users/phone/change/start',
    phone_change_confirm_by_email: 'users/phone/change/confirm_by_email',
    phone_change_new_phone: 'users/phone/change/new_phone',
    phone_change_confirm_by_phone: 'users/phone/change/confirm_by_phone'
  },
  videos: {
    item: {
      /**
       * Добавить видео в черновик
       * @param videoId Id видео
       */
      draft: (videoId: number) => `videos/${videoId}/draft_video`,
      /**
       * Опубликовать видео
       * @param videoId Id видео
       */
      publish: (videoId: number) => `videos/${videoId}/publicate_video`
    },
    /**
     * Статус кодирования видео
     * @param id - id видео
     */
    encoding_status: (id: number) => `videos/${id}/encoding_status`,
    /**
     * Активация / деактивация публичной ссылки на видео
     * @param id - id видео
     */
    share: (id: number) => `videos/${id}/share`,
    /**
     * Обновление метаданных видео (год, тип)
     * @param id - id видео
     */
    metadata: (id: number) => `videos/${id}/metadata`,
    /**
     * Инкремент счётчика просмотров видео
     * @param id - id видео
     */
    view: (id: number) => `videos/${id}/view`
  },
  /**
   * Публичная ссылка на видео
   * @param token - публичный токен видео
   */
  public_video: (token: string) => `public/videos/${token}`,
  notifications: {
    list: 'notifications',
    unviewedCount: 'notifications/unviewed_count',
    /**
     * Подробные данные уведомления
     * @param id - id уведомления
     */
    show: (id: number) => `notifications/${id}`,
    viewAll: 'notifications/view_all'
  },
  responses: {
    list: 'responses'
  },
  filterPresets: 'filter_presets',
  savedFilters: {
    list: 'saved_filters',
    /**
     * Сохранённый фильтр по id (переименование, удаление)
     * @param id - id фильтра
     */
    item: (id: number) => `saved_filters/${id}`
  },
  bookmarkFolders: {
    list: 'bookmark_folders',
    /**
     * Папка по id (переименование, удаление)
     * @param id - id папки
     */
    item: (id: number) => `bookmark_folders/${id}`,
    /**
     * Привязка элемента к папке: актёр у режиссёра, кастинг у актёра
     * @param folderId - id папки
     * @param targetId - id актёра или кастинга
     */
    addItem: (folderId: number, targetId: number) =>
      `bookmark_folders/${folderId}/items/${targetId}`,
    /**
     * Снятие привязки
     * @param folderId - id папки
     * @param targetId - id актёра или кастинга
     */
    removeItem: (folderId: number, targetId: number) =>
      `bookmark_folders/${folderId}/items/${targetId}`
  },
  menuCounters: 'menu_counters',
  feedbacks: 'feedbacks',
  filmOffers: {
    list: 'film_offers',
    /**
     * Данные одного предложения фильма
     * @param id - id предложения
     */
    show: (id: number) => `film_offers/${id}`,
    /**
     * Подтверждение предложения режиссёром
     * @param id - id предложения
     */
    confirm: (id: number) => `film_offers/${id}/confirm`,
    /**
     * Отклонение предложения режиссёром
     * @param id - id предложения
     */
    reject: (id: number) => `film_offers/${id}/reject`
  }
};
